import {Injectable, TemplateRef} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription, timer} from 'rxjs';
import {
  ChangedQuestionsInterface,
  GameEvent,
  GameInfo,
  GameMessage,
  GameplayState,
  GameState,
  HostMessage,
  MessagePopup,
  Team,
  TeamAnswer,
  TriviaGame,
  TriviaRoundQuestion
} from '@core/models';
import {UtilitiesService} from './utilities.service';
import {delayWhen, first, map, retryWhen, takeUntil, tap} from 'rxjs/operators';
import {GameHelper} from '@core/helpers';
import {BaseApiService} from '@core/api-services';
import {MessageService} from './message.service';
import {GameFormatsEnum, GameView} from '@core/enums';
import {Router} from '@angular/router';
import {RouterLinks} from '@core/constants';
import { RoundAnswers } from '@core/models/game/round-answers.interface';

@Injectable({
  providedIn: 'root'
})
export class GameService {
  gameplayState: BehaviorSubject<GameplayState> = new BehaviorSubject<GameplayState>({
    activeQuestionIndex: 0,
    activeRoundIndex: 0,
  });
  complete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  eventData: BehaviorSubject<GameEvent> = new BehaviorSubject<GameEvent>({} as GameEvent);
  questionTimer: BehaviorSubject<{ time: string, dateTime: string }[]> = new BehaviorSubject<{
    time: string,
    dateTime: string
  }[]>([]);
  gameScreen: BehaviorSubject<string> = new BehaviorSubject<string>('welcome');
  errView: BehaviorSubject<string> = new BehaviorSubject<string>('');
  questionTimerIsSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  changeRound: Subject<boolean> = new Subject<boolean>();
  teamsAccepted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  messageSubmitted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  lessThan2hours: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  activeTimerIsSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  leaderboardTeams: BehaviorSubject<Team[]> = new BehaviorSubject<Team[]>([]);
  gameChanged: Subject<boolean> = new Subject<boolean>();
  activeEventId: string;
  gameLiveTimer: BehaviorSubject<string> = new BehaviorSubject<string>('00:00:00');
  questionLiveTimer: BehaviorSubject<string> = new BehaviorSubject<string>('00:00:00');
  questions: BehaviorSubject<TriviaRoundQuestion[]> = new BehaviorSubject<TriviaRoundQuestion[]>([]);
  gameStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  onClickGameStarted: Subject<boolean> = new Subject<boolean>();
  onClickGameFinished: Subject<boolean> = new Subject<boolean>();
  questionStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  gameFinished: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  answersSet: BehaviorSubject<boolean[]> = new BehaviorSubject<boolean[]>([]);
  activeQuestionAnswerIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  selectedTabIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  splashScreenIndex: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  completedRoundsCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  scoringRoundsCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  acceptedQuestionAnswerIds: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  playerAnswers: RoundAnswers = {};
  changedQuestions: ChangedQuestionsInterface = {};
  game: BehaviorSubject<TriviaGame> = new BehaviorSubject<TriviaGame>({} as TriviaGame);
  private subscription: Subscription;
  private questionTimerSubscription: Subscription;
  private listenerEnd: Subject<boolean> = new Subject<boolean>();
  gameplayAcceptedQuestionIds: Set<string> = new Set();
  gameplayChangedQuestions: ChangedQuestionsInterface = {};
  gameplayActiveQuestionIndex = 0;
  btnTemplate: TemplateRef<any>;

  constructor(private baseApiService: BaseApiService,
              private router: Router,
              private messageService: MessageService) {
  }

  removeActiveEventFromStorage(eventId: string): void {
    if (this.isActiveEventId(eventId)) {
      this.cleanStorage();
      this.toInitialState();
    }
  }

  toInitialState(): void {
    this.gameplayState.next({
      activeQuestionIndex: 0,
      activeRoundIndex: 0,
    });
    this.questionTimer.next([]);
    this.questionTimerIsSet.next(false);
    this.gameStarted.next(false);
    this.gameFinished.next(false);
    this.onClickGameStarted.next(false);
    this.onClickGameFinished.next(false);
    this.answersSet.next([]);
    this.activeTimerIsSet.next(false);
    this.activeEventId = null;
    this.questionStarted.next(false);
    this.activeQuestionAnswerIndex.next(0);
    this.acceptedQuestionAnswerIds.next([]);
    this.selectedTabIndex.next(0);
    this.completedRoundsCount.next(0);
    this.scoringRoundsCount.next(0);
    this.splashScreenIndex.next(0);
    this.gameScreen.next('welcome');
    this.loading.next(false);
    this.subscription?.unsubscribe();
    this.gameLiveTimer.next('00:00:00');
    this.resetTimer();
    this.listenerEnd.next(true);
    this.lessThan2hours.next(false);
    this.leaderboardTeams.next([]);
    this.teamsAccepted.next(false);
    this.messageSubmitted.next(false);
    this.gameplayChangedQuestions = {};
    this.gameplayAcceptedQuestionIds.clear();
  }

  isActiveEventId(id: string): boolean {
    return id === localStorage.getItem('eventId');
  }

  runTimer(seconds: number = 0): void {
    this.subscription?.unsubscribe();
    this.subscription = timer(0, 1000)
      .subscribe(() => {
        ++seconds;
        this.gameLiveTimer.next(UtilitiesService.formatTime(seconds));
      });
  }

  cleanStorage(): void {
    localStorage.removeItem('eventId');
  }

  addInStorage(): void {
    if (this.activeEventId) {
      localStorage.setItem('eventId', this.activeEventId);
    } else {
      this.cleanStorage();
    }
  }

  runQuestionTimer(seconds = 0): void {
    seconds = isNaN(seconds) ? 0 : seconds;
    this.questionTimerSubscription?.unsubscribe();
    this.questionTimerSubscription = timer(0, 1000)
      .subscribe(() => {
        ++seconds;
        this.questionLiveTimer.next(UtilitiesService.formatTime(seconds));
      });
  }

  stopTimer(timerMode: string = 'subscription'): void {
    this[timerMode]?.unsubscribe();
  }

  resetTimer(timerMode: string = 'subscription', timerName: string = 'gameLiveTimer'): void {
    this[timerName].next('00:00:00');
    this[timerMode]?.unsubscribe();
  }

  nextRound(): void {
    this.gameplayState.next({
      activeQuestionIndex: 0,
      activeRoundIndex: this.completedRoundsCount.getValue(),
    });
    this.splashScreenIndex.next(this.completedRoundsCount.getValue());
    this.questionTimer.next([]);
    this.selectedTabIndex.next(0);
    this.questionStarted.next(false);
    this.gameScreen.next('round');
    this.questionTimerIsSet.next(false);
    const roundAnswersState: boolean[] = this.answersSet.value;
    roundAnswersState.push(true);
    this.answersSet.next(roundAnswersState);
    this.activeQuestionAnswerIndex.next(0);
    this.acceptedQuestionAnswerIds.next([]);
  }

  getGameState(): GameState {
    return {
      active_round_index: this.gameplayState.value.activeRoundIndex,
      active_question_index: this.gameplayState.value.activeQuestionIndex,
      questionStarted: this.questionStarted.value,
      acceptedQuestionAnswerIds: this.acceptedQuestionAnswerIds.value,
      game_finished: this.gameFinished.value,
      questionTimer: this.questionTimer.value,
      completedRoundsCount: this.completedRoundsCount.value,
      scoringRoundsCount: this.scoringRoundsCount.value,
      splashScreenIndex: this.splashScreenIndex.value,
      gameScreen: this.gameScreen.value,
      selectedTabIndex: this.selectedTabIndex.value,
      activeQuestionAnswerIndex: this.activeQuestionAnswerIndex.value,
      questionTimeIsSet: this.questionTimerIsSet.value,
      activeEventId: this.activeEventId,
      is_started: this.gameStarted.value,
      teamsAccepted: this.teamsAccepted.value
    };
  }

  setGameState(data: GameState): void {
    this.gameplayState.next({
      activeQuestionIndex: data.active_question_index || 0,
      activeRoundIndex: data.active_round_index || 0
    });
    this.acceptedQuestionAnswerIds.next(data.acceptedQuestionAnswerIds);
    this.questionTimer.next(data.questionTimer);
    this.completedRoundsCount.next(data.completedRoundsCount);
    this.scoringRoundsCount.next(data.scoringRoundsCount || 0);
    this.splashScreenIndex.next(data.splashScreenIndex);
    this.gameScreen.next(data.gameScreen);
    this.selectedTabIndex.next(data.selectedTabIndex);
    this.questionStarted.next(data.questionStarted);
    this.questionTimerIsSet.next(data.questionTimeIsSet);
    this.activeEventId = data.activeEventId;
    this.teamsAccepted.next(data.teamsAccepted);
    if (!localStorage.getItem('eventId')) {
      this.addInStorage();
    }
  }

  checkBonusRound(roundIndex: number): boolean {
    if (this.game.value.trivia_game?.trivia_game_rounds.length <= roundIndex) {
      return false;
    }
    return this.game.value.trivia_game
      ?.trivia_game_rounds[roundIndex].trivia_round.trivia_round_type.id === 'bonus';
  }

  checkTwoColumn(roundIndex: number): boolean {
    if (!this.checkBonusRound(roundIndex)) {
      return false;
    }
    return this.game.value.trivia_game
      ?.trivia_game_rounds[roundIndex].trivia_round.view_type.view_type_id === GameView.twoColumn;
  }

  tabValidation(): void {
    this.errView.next(null);
    if (!this.gameStarted.value &&
      !this.gameFinished.value &&
      this.eventData.value?.event_data?.game_code) {
      this.errView.next('pressStart');
      return;
    }

    if (!this.eventData.value?.event_data?.game_code &&
      !this.eventData.value?.event_data?.is_completed) {
      this.errView.next('notAvailable');
      return;
    }

    if ((this.gameStarted.value || this.eventData.value.event_data.is_completed) &&
      this.leaderboardTeams.value?.length === 0) {
      this.errView.next('noTeam');
    }
  }

  getGame(gameData: GameInfo, gameId: string = null): Observable<any> {
    return this.baseApiService.eventsService.getGame(gameData, gameId)
      .pipe(
        // retryWhen(errors =>
        //   errors.pipe(
        //     // restart in 5 seconds
        //     delayWhen(() => timer(5000))
        //   )
        // ),
        map(value => {
          value.trivia_game.trivia_game_rounds = GameHelper.sortGameRoundQuestions(value.trivia_game.trivia_game_rounds);
          value.trivia_game.trivia_game_rounds = GameHelper.setActiveAnswers(value.trivia_game.trivia_game_rounds);
          return value;
        }),
      );
  }

  updateGameplayState(newValues: {
    activeQuestionIndex?: number,
    activeRoundIndex?: number,
  }): void {
    const oldValue = {...this.gameplayState.value, ...newValues};
    this.gameplayState.next(oldValue);
  }

  gameFinishListener(finishFn: () => void): void {
    this.onClickGameFinished
      .pipe(takeUntil(this.listenerEnd))
      .subscribe((data: boolean) => {
        if (this.messageSubmitted.value) {
          if (data) {
            finishFn();
          }
          return;
        }
        if (data) {
          this.selectedTabIndex.next(0);
          const messageData: MessagePopup = {
            msg: 'Please submit your game notes before finishing the event',
            isConfirm: false
          };
          this.messageService.openConfirmModal(messageData);
        }
      });
  }

  saveMessage(message: HostMessage): Observable<GameMessage> {
    if (message.isSubmit) {
      return this.baseApiService.eventsService.submitGameMessage(message.eventId, message.host_message)
        .pipe(first(), map(() => {
          message.gameMessage.is_submitted = true;
          this.messageSubmitted.next(true);
          return message.gameMessage;
        }));
    }
    return this.baseApiService.eventsService.saveGameMessage(message.eventId, message.host_message)
      .pipe(first(), map(() => {
        message.gameMessage.is_saved = true;
        return message.gameMessage;
      }));
  }

  updateQuestionsList(roundIndex): void {
    if (this.game?.value?.trivia_game?.trivia_game_rounds) {
      const questions = this.game.value.trivia_game.trivia_game_rounds[roundIndex]
      .trivia_round.trivia_round_questions;
      this.questions.next(questions);
    }
  }

  navigateToGame(id: string, formatId: number): void {
    if (formatId === GameFormatsEnum['Trivial Warfare']) {
      this.router.navigate([RouterLinks.WARFARE_GAME, id]);
      return;
    }
    this.router.navigate([RouterLinks.GAME, id]);
  }

  // approveUserAnswers(roundId: string, delay: number = 0): void {
  //   this.loading.next(true);
  //   if (!!delay) {
  //     setTimeout(() => {
  //       this.makeApproveRequest(roundId);
  //     }, delay);
  //     return;
  //   }
  //   this.makeApproveRequest(roundId, true);
  // }

  // makeApproveRequest(roundId: string, isReload: boolean = false): void {
  //   this.baseApiService.gameApiService.approveTeamAnswers(roundId, this.eventData.value.event_data.event_id, isReload)
  //     .subscribe({
  //       next: () => {
  //         setTimeout(() => {
  //           this.gameChanged.next(true);
  //         }, 3000);
  //       },
  //       error: () => {
  //         this.loading.next(false);
  //       }
  //     });
  // }

  getAcceptedQuestions(round_id: string, code: string): Observable<ChangedQuestionsInterface> {
    code = String(code);
    if(!this.changedQuestions[round_id])
      return this.baseApiService.gameApiService.getAcceptedQuestions(round_id, code).pipe(
        tap(answers => this.changedQuestions = answers)
      );
    else
      return of(this.changedQuestions);
  }

  getPlayerAnswers(round_id: string): Observable<RoundAnswers> {
    const code = this.eventData.value.event_data.game_code;
    if (!this.playerAnswers[round_id])
     return this.baseApiService.gameApiService.getRoundAnswers(round_id, code.toString()).pipe(map(
      (answers => {
        answers = this.sortAnswers(answers)
        this.playerAnswers[round_id] = answers.reduce((acc, ans) => {

          if (!acc[ans.question_id]) {
            acc[ans.question_id] = [];
          }
          acc[ans.question_id].push(ans);
          return acc;
        }, {})
        return this.playerAnswers;
      })
    ))
    else {
      return of(this.playerAnswers);
    }
  }

  sortAnswers(answers: TeamAnswer[]) {
    for (const question of this.questions.value) {
      const correctAnswer = question.question.question_json.answer?.toString();

      return answers.sort((a, b) => {
        const aIsCorrect = a.answer === correctAnswer;
        const bIsCorrect = b.answer === correctAnswer;

        // Sort so that correct answers come first
        return (bIsCorrect ? 1 : 0) - (aIsCorrect ? 1 : 0);
      })
    }
  }

  public get getTriviaGame(): TriviaGame['trivia_game'] {
    return  this.game.value.trivia_game;
  }

}
